import { useEffect, type RefObject } from "react";
import { hideNative, openNative, setNativeBounds, showNative } from "../services/nativeBrowser";

const MAC_CHILD_WEBVIEW_TOP_OFFSET = 36;

export function useNativeBrowserGeometry(
  enabled: boolean,
  containerRef: RefObject<HTMLElement | null>,
  url: string | null,
  topBoundaryRef?: RefObject<HTMLElement | null>
): void {
  useEffect(() => {
    if (!enabled || !url) {
      void hideNative();
      return;
    }

    let disposed = false;
    let frame = 0;

    const measureBounds = () => {
      const node = containerRef.current;
      if (!node) return null;
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return null;
      const boundary = topBoundaryRef?.current?.getBoundingClientRect();
      const top = boundary ? Math.max(rect.top, boundary.bottom) : rect.top;
      const chromeOffset = Math.max(
        0,
        window.outerHeight - window.innerHeight,
        navigator.userAgent.includes("Mac OS X") ? MAC_CHILD_WEBVIEW_TOP_OFFSET : 0
      );
      const y = top + chromeOffset;
      return {
        x: rect.left,
        y,
        width: rect.width,
        height: Math.max(1, rect.bottom - y),
      };
    };

    const sync = () => {
      if (disposed) return;
      const bounds = measureBounds();
      if (!bounds) return;
      void setNativeBounds(bounds)
        .then(() => showNative())
        .catch(() => {});
    };

    const open = () => {
      const bounds = measureBounds();
      if (!bounds) return;
      void openNative(bounds, url)
        .then(() => showNative())
        .catch(() => {});
    };

    const hide = () => {
      void hideNative();
    };

    open();
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(sync);
    });
    if (containerRef.current) observer.observe(containerRef.current);
    window.addEventListener("resize", sync);
    window.addEventListener("focus", open);
    window.addEventListener("blur", hide);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", sync);
      window.removeEventListener("focus", open);
      window.removeEventListener("blur", hide);
      void hideNative();
    };
  }, [containerRef, enabled, topBoundaryRef, url]);
}
